import type { WebContainer } from '@webcontainer/api';
import { writeHotEdit } from './hotEditHistory';
import { getMergedFileContent } from '../utils/mergedFiles';
import { workbenchStore } from '../stores/workbench';
import { isDesignSystemFilePath } from './editSource';
import type { HotEditListContext } from './listContext';
import { appDataStore, notifyAppDataChanged } from '../stores/appData';

/**
 * JSX patching pulls in `@babel/*`, which expects `process.env.NODE_ENV` in the browser.
 * Dynamic import keeps Babel out of the initial bundle and avoids crashing app startup if `process`
 * is missing; Vite still should define `process.env.NODE_ENV` for when this chunk loads.
 */
export async function persistMergedClassName(opts: {
  /** Null with the light preview: the file store is written, and the preview recompiles. */
  wc: WebContainer | null;
  filePath: string;
  lineNumber: number;
  /** Tailwind utility deltas only — appended at call site, never replaces existing className */
  cnAppendOnly: string;
  /** Classes to take back out, e.g. `hidden` when the founder unhides an element. */
  cnRemove?: string;
  /** Keep classes the delta conflicts with, so hiding does not lose the element's display. */
  cnKeepConflicting?: boolean;
  columnNumber?: number;
  tagName?: string;
  componentName?: string;
  listContext?: HotEditListContext | null;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (isDesignSystemFilePath(opts.filePath)) {
    return { ok: false, error: 'Refusing to modify shared UI component file.' };
  }

  const append = opts.cnAppendOnly?.trim() ?? '';
  const remove = opts.cnRemove?.trim() ?? '';
  if (!append && !remove) return { ok: true };

  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const readFile = (p: string) => getMergedFileContent(p);

  const { patchJsxClassNameAtLine } = await import('./jsxPatcher');
  const r = patchJsxClassNameAtLine(source, opts.lineNumber, '', {
    cnAppendOnly: append,
    cnRemove: remove,
    cnKeepConflicting: opts.cnKeepConflicting,
    columnNumber: opts.columnNumber,
    tagName: opts.tagName,
    componentName: opts.componentName,
    listContext: opts.listContext,
    rootFilePath: opts.filePath,
    readFile,
  });
  if (!r.ok) return r;
  const outPath = r.writtenFilePath ?? opts.filePath;
  const outContent = r.content;
  if (outContent === getMergedFileContent(outPath)) return { ok: true };
  await writeHotEdit(opts.wc, outPath, outContent);
  return { ok: true };
}

export async function persistTextEdit(opts: {
  /** Null with the light preview: the file store is written, and the preview recompiles. */
  wc: WebContainer | null;
  filePath: string;
  lineNumber: number;
  text: string;
  columnNumber?: number;
  tagName?: string;
  componentName?: string;
  hintText?: string;
  listContext?: HotEditListContext | null;
}): Promise<{ ok: true; writtenFilePath?: string } | { ok: false; error: string }> {
  if (isDesignSystemFilePath(opts.filePath)) {
    return {
      ok: false,
      error: 'Text edits must land on the page call site — not inside shared UI components.',
    };
  }

  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const readFile = (p: string) => getMergedFileContent(p);

  const { patchJsxTextAtLine } = await import('./jsxPatcher');
  const r = patchJsxTextAtLine(source, opts.lineNumber, opts.text, {
    columnNumber: opts.columnNumber,
    tagName: opts.tagName,
    componentName: opts.componentName,
    hintText: opts.hintText,
    listContext: opts.listContext,
    rootFilePath: opts.filePath,
    readFile,
    filePaths: Object.keys(workbenchStore.files.get()),
  });
  if (!r.ok && r.liveRecord) {
    // A row of the startup's live data (US 596): saved there, and the preview reloads it.
    const saveLive = appDataStore.liveEdit.get();
    if (!saveLive) return { ok: false, error: r.error };
    const saved = await saveLive(r.liveRecord);
    if (!saved.ok) return saved;
    notifyAppDataChanged(saved.entity);
    return { ok: true };
  }
  if (!r.ok) return r;
  const outPath = r.writtenFilePath ?? opts.filePath;
  const outContent = r.content;
  if (outContent === getMergedFileContent(outPath)) return { ok: true, writtenFilePath: outPath };
  await writeHotEdit(opts.wc, outPath, outContent);
  return { ok: true, writtenFilePath: outPath };
}

export async function persistSelectOptions(opts: {
  /** Null with the light preview: the file store is written, and the preview recompiles. */
  wc: WebContainer | null;
  filePath: string;
  lineNumber: number;
  options: string[];
  columnNumber?: number;
  tagName?: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (isDesignSystemFilePath(opts.filePath)) {
    return {
      ok: false,
      error: 'Select edits must land on the page call site — not inside shared UI components.',
    };
  }

  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const { patchJsxSelectOptionsAtLine } = await import('./jsxPatcher');
  const r = patchJsxSelectOptionsAtLine(source, opts.lineNumber, opts.options, {
    columnNumber: opts.columnNumber,
    tagName: opts.tagName ?? 'select',
  });
  if (!r.ok) return r;
  if (r.content === source) return { ok: true };
  await writeHotEdit(opts.wc, opts.filePath, r.content);
  return { ok: true };
}

export async function persistJsxAttributeLiteral(opts: {
  /** Null with the light preview: the file store is written, and the preview recompiles. */
  wc: WebContainer | null;
  filePath: string;
  lineNumber: number;
  attributeName: string;
  literalValue: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const { patchJsxAttributeLiteralAtLine } = await import('./jsxPatcher');
  const r = patchJsxAttributeLiteralAtLine(
    source,
    opts.lineNumber,
    opts.attributeName,
    opts.literalValue,
  );
  if (!r.ok) return r;
  if (r.content === source) return { ok: true };
  await writeHotEdit(opts.wc, opts.filePath, r.content);
  return { ok: true };
}

/**
 * A colour written into the prototype's design system, not onto one element: `--primary` in
 * `src/index.css`, which every `bg-primary` in the prototype then follows.
 */
export async function persistThemeToken(opts: {
  wc: WebContainer | null;
  token: string;
  /** HSL parts, the shape the stylesheet stores: `175.3 77.4% 26.1%`. */
  value: string;
  cssPath?: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const path = opts.cssPath ?? 'src/index.css';
  const source = getMergedFileContent(path);
  if (!source) return { ok: false, error: `This prototype has no ${path} to change the colour in.` };

  const { setTokenInCss } = await import('./designTokens');
  const out = setTokenInCss(source, opts.token, opts.value);
  if (out == null) return { ok: false, error: `The design system has no ${opts.token} colour to change.` };
  if (out === source) return { ok: true };
  await writeHotEdit(opts.wc, path, out);
  return { ok: true };
}

/**
 * Another picture in place of this one (Prototype Editor UX plan, step 6). The new reference is
 * written where the old one was: in the list's data when the picture comes from a record the
 * founder clicked, otherwise as the <img>'s own `src`. The bytes are already in the store by the
 * time this runs, so the recompile shows them.
 */
export async function persistImageReplace(opts: {
  wc: WebContainer | null;
  filePath: string;
  lineNumber: number;
  reference: string;
  fromStorePath: string | null;
  recordId?: string | null;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (opts.recordId && opts.fromStorePath) {
    const { findListData, setCell } = await import('./listDataFile');
    const { imageFieldOf } = await import('./imageReplace');
    const list = findListData({
      recordId: opts.recordId,
      filePaths: Object.keys(workbenchStore.files.get()),
      readFile: (p) => getMergedFileContent(p),
    });
    const field = list ? imageFieldOf(list, opts.recordId, opts.fromStorePath) : null;
    if (list && field) {
      const content = setCell(list, opts.recordId, field, opts.reference);
      if (content == null) return { ok: false, error: "The picture's field could not be written." };
      if (content !== list.source) await writeHotEdit(opts.wc, list.filePath, content);
      return { ok: true };
    }
  }
  const r = await persistJsxAttributeLiteral({
    wc: opts.wc,
    filePath: opts.filePath,
    lineNumber: opts.lineNumber,
    attributeName: 'src',
    literalValue: opts.reference,
  });
  if (!r.ok && /too dynamic/.test(r.error)) {
    return { ok: false, error: 'This picture comes from data the editor could not find. Ask Ideoz to change it.' };
  }
  return r;
}

/**
 * Another icon in place of this one (UI pass, issue 5). The prototype draws Lucide components,
 * so the change is the name in the JSX and the import that brings it in.
 */
export async function persistIconSwap(opts: {
  wc: WebContainer | null;
  filePath: string;
  lineNumber: number;
  iconName: string;
  columnNumber?: number;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (isDesignSystemFilePath(opts.filePath)) {
    return { ok: false, error: 'Refusing to modify shared UI component file.' };
  }
  const source = getMergedFileContent(opts.filePath);
  if (!source) return { ok: false, error: `File not found in workspace: ${opts.filePath}` };

  const { patchJsxIconAtLine } = await import('./jsxPatcher');
  const r = patchJsxIconAtLine(source, opts.lineNumber, opts.iconName, {
    columnNumber: opts.columnNumber,
  });
  if (!r.ok) return r;
  if (r.content === source) return { ok: true };
  await writeHotEdit(opts.wc, opts.filePath, r.content);
  return { ok: true };
}

/**
 * A list's data file as the records sheet composed it (UI pass, issue 3).
 *
 * Every cell used to write and recompile on the spot, which reloaded the preview and dropped the
 * founder out of whatever they were editing. The sheet now hands the finished file to Apply with
 * the rest of the changes.
 */
export async function persistDataFile(opts: {
  wc: WebContainer | null;
  filePath: string;
  content: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (opts.content === getMergedFileContent(opts.filePath)) return { ok: true };
  await writeHotEdit(opts.wc, opts.filePath, opts.content);
  return { ok: true };
}
